import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { isManager, canEdit } from "../lib/roles";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useSettings, logoUrl } from "../context/SettingsContext";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import StudentFormDialog from "../components/StudentFormDialog";
import GenderIcon from "../components/GenderIcon";
import { Check, X, ChevronRight, ChevronLeft, FileSpreadsheet, Eraser, Loader2, Users, CalendarCheck, CalendarX, Search, UserPlus, Trash2, Printer, Plane } from "lucide-react";

function today() { return new Date().toISOString().slice(0, 10); }

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <div className="surface-card flex items-center gap-4 rounded-xl border border-border bg-card p-4">
      <div className="flex h-11 w-11 items-center justify-center rounded-lg" style={{ backgroundColor: tint + "22", color: tint }}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-2xl font-semibold text-foreground">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

export default function Attendance() {
  const { t, dir } = useLang();
  const { user } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const isSuper = isManager(user);
  const editable = canEdit(user);

  const [date, setDate] = useState(today());
  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState("all");
  const [search, setSearch] = useState("");
  const [students, setStudents] = useState([]);
  const [records, setRecords] = useState({});
  const [exporting, setExporting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [groupReport, setGroupReport] = useState(null);

  const searching = search.trim().length > 0;

  const loadGroups = useCallback(async () => {
    try { const r = await api.get("/groups"); setGroups(r.data); } catch (err) { console.error("Failed to load groups", err); }
  }, []);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  const loadStudents = useCallback(async () => {
    const params = searching ? { search: search.trim() } : (group === "all" ? {} : { group_id: group });
    const r = await api.get("/students", { params });
    setStudents(r.data);
  }, [group, search, searching]);

  const loadAttendance = useCallback(async () => {
    const params = (!searching && group !== "all") ? { date, group_id: group } : { date };
    const r = await api.get("/attendance", { params });
    const map = {};
    r.data.forEach((rec) => { map[rec.student_id] = { id: rec.id, status: rec.status }; });
    setRecords(map);
  }, [date, group, searching]);

  useEffect(() => { const id = setTimeout(loadStudents, 250); return () => clearTimeout(id); }, [loadStudents]);
  useEffect(() => { loadAttendance(); }, [loadAttendance]);

  const mark = async (studentId, status) => {
    try {
      const { data } = await api.post("/attendance/mark", { student_id: studentId, date, status });
      setRecords((p) => ({ ...p, [studentId]: { id: data.id, status: data.status } }));
      toast.success(t("attendance.saved"));
    } catch { toast.error("Error"); }
  };

  const clearMark = async (studentId) => {
    try {
      await api.delete(`/attendance/by/${studentId}/${date}`);
      setRecords((p) => { const n = { ...p }; delete n[studentId]; return n; });
      toast.success(t("attendance.cleared"));
    } catch { toast.error("Error"); }
  };

  const deleteStudent = async (s) => {
    if (!window.confirm(t("attendance.confirmDeleteStudent"))) return;
    try {
      await api.delete(`/students/${s.id}`);
      toast.success(t("attendance.studentDeleted"));
      loadStudents();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deleteAllInGroup = async () => {
    if (group === "all") return;
    const grpName = groups.find((g) => g.id === group)?.name || "";
    if (!window.confirm(t("attendance.confirmDeleteAllInGroup").replace("{group}", grpName))) return;
    try {
      const { data } = await api.post("/students/delete-by-group", { group_id: group });
      toast.success(`${t("attendance.groupCleared")} (${data.deleted})`);
      loadStudents();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const printGroup = async () => {
    if (group === "all") return;
    try {
      const { data } = await api.get(`/groups/${group}/report`);
      setGroupReport(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!groupReport) return;
    const id = setTimeout(() => window.print(), 150);
    const clear = () => setGroupReport(null);
    window.addEventListener("afterprint", clear);
    return () => { clearTimeout(id); window.removeEventListener("afterprint", clear); };
  }, [groupReport]);

  const exportExcel = async (grpId, grpName) => {    setExporting(true);
    try {
      const params = grpId ? { group_id: grpId } : {};
      const r = await api.get("/export/excel", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = grpName ? `attendance_${grpName}.xlsx` : "attendance_all_groups.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  const stats = useMemo(() => {
    let present = 0, absent = 0, leave = 0;
    students.forEach((s) => { const r = records[s.id]; if (r?.status === "present") present++; else if (r?.status === "absent") absent++; else if (r?.status === "leave") leave++; });
    return { present, absent, leave, total: students.length };
  }, [records, students]);

  const Chevron = dir === "rtl" ? ChevronLeft : ChevronRight;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground">{t("attendance.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("attendance.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{t("attendance.date")}</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="attendance-date" className="w-[160px]" dir="ltr" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{t("attendance.group")}</label>
            <Select value={group} onValueChange={setGroup} disabled={searching}>
              <SelectTrigger className="w-[190px]" data-testid="group-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("attendance.allGroups")}</SelectItem>
                {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* search + add */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("attendance.search")} data-testid="student-search" className="ps-9" />
        </div>
        {editable && (
          <button onClick={() => setShowAdd(true)} data-testid="add-student-btn"
            className="brand-btn inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90">
            <UserPlus className="h-4 w-4" /> {t("attendance.addStudent")}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard icon={Users} label={t("attendance.totalStudents")} value={stats.total} tint="#4F46E5" />
        <StatCard icon={CalendarCheck} label={t("attendance.presentCount")} value={stats.present} tint="#10B981" />
        <StatCard icon={CalendarX} label={t("attendance.absentCount")} value={stats.absent} tint="#EF4444" />
        <StatCard icon={Plane} label={t("attendance.leaveCount")} value={stats.leave} tint="#F59E0B" />
      </div>

      {!searching && (
        <div className="flex flex-wrap items-center gap-3">
          {group !== "all" ? (
            <>
              <button onClick={() => exportExcel(group, groups.find((g) => g.id === group)?.name)} disabled={exporting} data-testid="export-group-btn"
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary disabled:opacity-60">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-emerald-600" />}
                {t("attendance.exportGroup")}
              </button>
              {editable && <button onClick={deleteAllInGroup} data-testid="delete-all-group-btn"
                className="inline-flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10">
                <Trash2 className="h-4 w-4" /> {t("attendance.deleteAllInGroup")}
              </button>}
              <button onClick={printGroup} data-testid="print-group-btn"
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary">
                <Printer className="h-4 w-4 brand-text" /> {t("attendance.printGroup")}
              </button>
            </>
          ) : (
            groups.map((g) => (
              <button key={g.id} onClick={() => exportExcel(g.id, g.name)} disabled={exporting} data-testid={`export-group-${g.id}`}
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary disabled:opacity-60">
                <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                {g.name}
              </button>
            ))
          )}
          <button onClick={() => exportExcel(null)} disabled={exporting} data-testid="export-all-btn"
            className="brand-btn inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-60">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            {t("attendance.exportAll")}
          </button>
        </div>
      )}

      {searching && <div className="text-sm text-muted-foreground" data-testid="search-results-label">{t("attendance.searchResults")}: {students.length}</div>}

      <div className="surface-card overflow-hidden rounded-xl border border-border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/60">
                <th className="px-4 py-3 text-start font-medium text-muted-foreground w-16">{t("attendance.number")}</th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("attendance.student")}</th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("attendance.group")}</th>
                <th className="px-4 py-3 text-center font-medium text-muted-foreground">{t("attendance.status")}</th>
                <th className="px-4 py-3 text-center font-medium text-muted-foreground">{t("attendance.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {students.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">{t("attendance.noStudents")}</td></tr>
              )}
              {students.map((s) => {
                const rec = records[s.id];
                return (
                  <tr key={s.id} className="border-b border-border last:border-0 transition-colors hover:bg-secondary/40" data-testid={`student-row-${s.id}`}>
                    <td className="px-4 py-3 table-cell-pad text-muted-foreground font-mono text-xs">{s.number}</td>
                    <td className="px-4 py-3 table-cell-pad">
                      <div className="flex items-center gap-2">
                        <GenderIcon gender={s.gender} />
                        <button onClick={() => navigate(`/student/${s.id}`)} data-testid={`student-name-${s.id}`}
                          className="font-medium text-foreground hover:brand-text hover:underline text-start">
                          {s.name}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 table-cell-pad text-muted-foreground">{s.group}</td>
                    <td className="px-4 py-3 table-cell-pad text-center">
                      {rec ? (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${rec.status === "present" ? "bg-emerald-100 text-emerald-700" : rec.status === "leave" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                          {rec.status === "present" ? <Check className="h-3.5 w-3.5" /> : rec.status === "leave" ? <Plane className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                          {rec.status === "present" ? t("attendance.present") : rec.status === "leave" ? t("attendance.leave") : t("attendance.absent")}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">{t("attendance.notMarked")}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 table-cell-pad">
                      <div className="flex items-center justify-center gap-2">
                        {editable && <>
                        <button onClick={() => mark(s.id, "present")} data-testid={`mark-present-${s.id}`} aria-label={t("attendance.markPresent")}
                          className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-all active:scale-90 ${rec?.status === "present" ? "border-emerald-500 bg-emerald-500 text-white" : "border-border text-emerald-600 hover:bg-emerald-50"}`}>
                          <Check className="h-4 w-4" />
                        </button>
                        <button onClick={() => mark(s.id, "absent")} data-testid={`mark-absent-${s.id}`} aria-label={t("attendance.markAbsent")}
                          className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-all active:scale-90 ${rec?.status === "absent" ? "border-red-500 bg-red-500 text-white" : "border-border text-red-600 hover:bg-red-50"}`}>
                          <X className="h-4 w-4" />
                        </button>
                        <button onClick={() => mark(s.id, "leave")} data-testid={`mark-leave-${s.id}`} aria-label={t("attendance.markLeave")}
                          className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-all active:scale-90 ${rec?.status === "leave" ? "border-amber-500 bg-amber-500 text-white" : "border-border text-amber-600 hover:bg-amber-50"}`}>
                          <Plane className="h-4 w-4" />
                        </button>
                        {isSuper && rec && (
                          <button onClick={() => clearMark(s.id)} data-testid={`clear-mark-${s.id}`} aria-label={t("attendance.clear")}
                            className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-secondary">
                            <Eraser className="h-4 w-4" />
                          </button>
                        )}
                        <button onClick={() => deleteStudent(s)} data-testid={`delete-student-${s.id}`} aria-label={t("attendance.deleteStudent")}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-destructive transition-colors hover:bg-destructive/10">
                          <Trash2 className="h-4 w-4" />
                        </button>
                        </>}
                        <button onClick={() => navigate(`/student/${s.id}`)} data-testid={`history-${s.id}`} aria-label={t("attendance.viewHistory")}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-secondary">
                          <Chevron className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <StudentFormDialog open={showAdd} onOpenChange={setShowAdd} groups={groups} student={null}
        presetGroupId={group !== "all" ? group : undefined} onSaved={() => { loadStudents(); }} />

      {groupReport && (
        <div id="print-report" data-testid="group-print-report">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "2px solid #111", paddingBottom: 16, marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              {logoUrl(settings.logo_path) ? <img src={logoUrl(settings.logo_path)} alt="logo" style={{ height: 56, width: 56, objectFit: "cover", borderRadius: 8 }} /> : null}
              <div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{settings.school_name}</div>
                <div style={{ fontSize: 13, color: "#444" }}>{t("attendance.rosterTitle")} — {groupReport.group.name}</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#444", textAlign: dir === "rtl" ? "left" : "right" }}>
              {t("detail.generated")}: {new Date().toISOString().slice(0, 10)}
              <div>{t("attendance.totalStudents")}: {groupReport.students.length}</div>
            </div>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                {[t("attendance.number"), t("attendance.student"), t("detail.gender"), t("detail.phone1"), t("detail.phone2"), t("attendance.totalPresent"), t("attendance.totalAbsent"), t("attendance.leave"), t("detail.percentage"), t("detail.notes")].map((h, i) => (
                  <th key={i} style={{ border: "1px solid #999", padding: 6, background: "#f0f0f0", textAlign: dir === "rtl" ? "right" : "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groupReport.students.map((s) => (
                <tr key={s.id}>
                  <td style={{ border: "1px solid #999", padding: 6 }}>{s.number}</td>
                  <td style={{ border: "1px solid #999", padding: 6 }}>{s.name}</td>
                  <td style={{ border: "1px solid #999", padding: 6 }}>{s.gender === "male" ? t("student.male") : s.gender === "female" ? t("student.female") : ""}</td>
                  <td style={{ border: "1px solid #999", padding: 6 }}>{s.phone1}</td>
                  <td style={{ border: "1px solid #999", padding: 6 }}>{s.phone2}</td>
                  <td style={{ border: "1px solid #999", padding: 6, textAlign: "center" }}>{s.present}</td>
                  <td style={{ border: "1px solid #999", padding: 6, textAlign: "center" }}>{s.absent}</td>
                  <td style={{ border: "1px solid #999", padding: 6, textAlign: "center" }}>{s.leave}</td>
                  <td style={{ border: "1px solid #999", padding: 6, textAlign: "center" }}>{s.percentage}%</td>
                  <td style={{ border: "1px solid #999", padding: 6 }}>{s.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
